import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Github, Menu, X } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { AicostrMark } from '@/components/icons/AicostrMark';

/**
 * Sticky glass navigation. The landing page is always dark, so the theme
 * toggle lives in the app shell only — not here.
 */
export function LandingNav() {
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <nav className="glass sticky top-0 z-50 border-b border-hairline">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between gap-4 px-4 sm:px-6 lg:px-8">
        <button
          onClick={() => navigate('/')}
          className="group flex items-center gap-2.5 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          aria-label="aicostr home"
        >
          <AicostrMark size={32} />
          <span className="text-base font-semibold tracking-tight md:text-lg">
            <span className="text-primary">ai</span>costr
          </span>
        </button>

        <div className="hidden items-center gap-1 md:flex">
          <NavLink href="#how" label="How it works" />
          <NavLink href="#features" label="Features" />
          <NavLink href="#pricing" label="Pricing" />
          <NavLink
            href="https://github.com/Vinodmurkute/ai-cost-guard"
            label="GitHub"
            icon={Github}
            external
          />
          <div className="mx-2 h-5 w-px bg-border" aria-hidden />
          <Button variant="ghost" size="sm" onClick={() => navigate('/login')}>
            Sign in
          </Button>
          <Button size="sm" onClick={() => navigate('/signup')}>
            Get started
            <ArrowRight />
          </Button>
        </div>

        <div className="flex items-center gap-1 md:hidden">
          <Button
            variant="ghost"
            size="icon"
            aria-label="Toggle menu"
            onClick={() => setMobileOpen((o) => !o)}
          >
            {mobileOpen ? <X /> : <Menu />}
          </Button>
        </div>
      </div>

      {mobileOpen && (
        <div className="border-t border-hairline bg-background md:hidden">
          <div className="mx-auto flex max-w-7xl flex-col gap-1 px-4 py-3 sm:px-6">
            {[
              { href: '#how', label: 'How it works' },
              { href: '#features', label: 'Features' },
              { href: '#pricing', label: 'Pricing' },
              { href: '#faq', label: 'FAQ' },
            ].map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setMobileOpen(false)}
                className="rounded-md px-3 py-2 text-sm text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
              >
                {l.label}
              </a>
            ))}
            <a
              href="https://github.com/Vinodmurkute/ai-cost-guard"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setMobileOpen(false)}
              className="flex items-center gap-2 rounded-md px-3 py-2 text-sm text-muted-foreground hover:bg-accent hover:text-foreground"
            >
              <Github className="size-4" />
              GitHub
            </a>
            <div className="my-1 h-px bg-border" />
            <Button
              variant="outline"
              onClick={() => {
                setMobileOpen(false);
                navigate('/login');
              }}
            >
              Sign in
            </Button>
            <Button
              onClick={() => {
                setMobileOpen(false);
                navigate('/signup');
              }}
            >
              Get started
            </Button>
          </div>
        </div>
      )}
    </nav>
  );
}

function NavLink({
  href,
  label,
  icon: Icon,
  external,
}: {
  href: string;
  label: string;
  icon?: typeof Github;
  external?: boolean;
}) {
  return (
    <a
      href={href}
      target={external ? '_blank' : undefined}
      rel={external ? 'noopener noreferrer' : undefined}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium text-muted-foreground',
        'transition-colors duration-150 ease-out-quart hover:bg-accent hover:text-foreground',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
      )}
    >
      {Icon && <Icon className="size-3.5" />}
      {label}
    </a>
  );
}
